// Questions d'angle avant la lettre (issue #165). Pour chaque question, des
// propositions tirées de l'offre et du CV (un clic) et un champ libre pour le
// dire avec ses mots. Partagé par l'extension, Radar et la console : chacun
// pose ses questions par son propre transport IA et passe ici le résultat.
//
// Répondre ou passer écrit la lettre dans les deux cas : une seule action mise
// en avant, comme les questions d'écart.

import { useEffect, useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import type { TranslateFn } from './context'
import { anglesRemplis, type LetterAngle, type LetterAngleQuestion } from '@ai-job-fit/engine/letterAngles'

export interface LetterAnglesModalProps {
  questions: LetterAngleQuestion[]
  /** Reçoit les réponses non vides ; l'hôte écrit la lettre. */
  onSubmit: (angles: LetterAngle[]) => Promise<void> | void
  /** Écrire sans angle. */
  onSkip: () => void
  /** Fermer sans écrire (Échap). Absent, Échap ne fait rien. */
  onCancel?: () => void
  t: TranslateFn
}

export default function LetterAnglesModal({ questions, onSubmit, onSkip, onCancel, t }: LetterAnglesModalProps) {
  // Une réponse par question : la proposition cliquée, ou le texte tapé, ou
  // les deux (« proposition. précision »).
  const [choix, setChoix] = useState<string[]>(() => questions.map(() => ''))
  const [libre, setLibre] = useState<string[]>(() => questions.map(() => ''))
  const [busy, setBusy] = useState(false)

  const reponses = questions.map((_, i) => [choix[i], libre[i].trim()].filter(Boolean).join('. '))

  const envoyer = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onSubmit(anglesRemplis(questions, reponses))
    } finally {
      setBusy(false)
    }
  }

  // Échap ferme CETTE fenêtre, jamais l'aperçu ouvert derrière (issue #176) :
  // l'aperçu de l'extension se ferme à Échap et vide l'historique, donc le CV
  // généré partait avec. Écouté en capture sur `window`, avant l'aperçu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      if (!busy) onCancel?.()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [busy, onCancel])

  const maj = (setter: typeof setChoix, i: number, v: string) =>
    setter((prev) => prev.map((x, j) => (j === i ? v : x)))

  return (
    <div className="angles-backdrop" role="dialog" aria-modal="true" aria-label={t('letterAngles.title')} data-testid="letter-angles">
      <div className="angles-modal">
        <h3 className="angles-title">{t('letterAngles.title')}</h3>
        <p className="angles-intro">{t('letterAngles.intro')}</p>
        {questions.map((q, i) => (
          <div key={q.id} className="angles-question">
            <div className="angles-label">{q.question}</div>
            {q.options.length > 0 && (
              <div className="angles-options">
                {q.options.map((o) => (
                  <button
                    key={o}
                    type="button"
                    className={`angles-option${choix[i] === o ? ' on' : ''}`}
                    aria-pressed={choix[i] === o}
                    onClick={() => maj(setChoix, i, choix[i] === o ? '' : o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              className="angles-input"
              value={libre[i]}
              placeholder={t('letterAngles.placeholder')}
              onChange={(e) => maj(setLibre, i, e.target.value)}
              data-testid={`letter-angles-input-${i}`}
            />
          </div>
        ))}
        <div className="angles-actions">
          <button type="button" className="angles-skip" onClick={onSkip} disabled={busy}>
            {t('letterAngles.skip')}
          </button>
          <button type="button" className="angles-write" onClick={() => void envoyer()} disabled={busy} data-testid="letter-angles-write">
            {busy ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />}
            {t('letterAngles.write')}
          </button>
        </div>
      </div>
    </div>
  )
}
